import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import InsightNavigator from './components/InsightNavigator';
import AlertsPanel from './components/AlertsPanel';
import SEOTooltip from './components/SEOTooltip';
import StrategyDashboard from './components/StrategyDashboard';
import AsIsState from './components/AsIsState';
import GoalSetting from './components/GoalSetting';
import ActionPlan from './components/ActionPlan';
import GovernanceOverviewDashboard from './components/GovernanceOverviewDashboard';
import GovernanceAsIsPerformance from './components/GovernanceAsIsPerformance';
import CompetitorBenchmark from './components/CompetitorBenchmark';
import GoalsTracking from './components/GoalsTracking';
import ActionPlanTracking from './components/ActionPlanTracking';
import Blog from './components/Blog';
import FAQ from './components/FAQ';
import ImplementationOverview from './components/ImplementationOverview';
import OptimizationOverview from './components/OptimizationOverview';
import EcosystemMonitor from './components/EcosystemMonitor';
import ForesightRecommends from './components/ForesightRecommends';
import LockedPlaceholder from './components/LockedPlaceholder';
import OnPageContent from './components/OnPageContent';
import ImageOptimizer from './components/ImageOptimizer';
import MetaTitleGenerator from './components/MetaTitleGenerator';
import OffPageContent from './components/OffPageContent';
import SocialBookmarkingDetail from './components/SocialBookmarkingDetail';
import ProfileBusinessListingDetail from './components/ProfileBusinessListingDetail';
import DirectorySubmissionDetail from './components/DirectorySubmissionDetail';
import QaSubmissionDetail from './components/QaSubmissionDetail';
import ImageSubmissionWrapper from './components/ImageSubmissionWrapper';
import ClassifiedAdsWrapper from './components/ClassifiedAdsWrapper';
import PDFSubmissionWrapper from './components/PDFSubmissionWrapper';
import Web2ArticleWrapper from './components/Web2ArticleWrapper';
import strategyService, { type DashboardStatus } from '../../services/strategyService';
import { DASHBOARD_SECTIONS } from '../../constants/telemetryModules';
import { usePageTracking } from '../../hooks/usePageTracking';

// Sections that require keywords to be locked -- maps section key to display name
const GATED_SECTIONS: Record<string, string> = {
  'as-is-state': 'AS-IS State',
  'goal-setting': 'Goal Setting',
  'action-plan': 'Action Plan',
  'implementation-overview': 'Implementation',
  'blog': 'Implementation',
  'faq': 'Implementation',
  'on-page-content': 'Implementation',
  'image-optimizer': 'Implementation',
  'meta-title-generator': 'Implementation',
  'off-page-content': 'Implementation',
  'image-submission': 'Implementation',
  'classified-ads': 'Implementation',
  'pdf-submission': 'Implementation',
  'web2-submission': 'Implementation',
  'governance-overview': 'Governance',
  'as-is-performance': 'Governance',
  'goals-tracking': 'Governance',
  'action-plan-tracking': 'Governance',
  'competitor-benchmark': 'Governance',
  'optimization-overview': 'Optimization',
  'ecosystem-monitor': 'Optimization',
  'foresight-recommends': 'Optimization',
};

// Mocking the 20 Locked Keywords array for the entire module
const LOCKED_KEYWORDS = [
  "enterprise cloud",
  "b2b saas",
  "cloud migration",
  "seo services in kolkata",
  "digital marketing agency",
  "best seo agency",
  "content marketing",
  "cloud infrastructure",
  "aws migration",
  "saas scaling",
  "b2b lead generation",
  "technical seo",
  "local seo services",
  "ecommerce seo",
  "link building strategy",
  "content strategy",
  "website optimization",
  "cloud security",
  "data center migration",
  "saas marketing"
];

export default function Dashboard() {
  const [activeSection, setActiveSection] = useState('strategy-dashboard');
  const location = useLocation();
  const navigate = useNavigate();
  const userId = localStorage.getItem('user_id') || '';

  const [darkMode, setDarkMode] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipContent, setTooltipContent] = useState({ title: '', content: '' });
  const [dashboardStatus, setDashboardStatus] = useState<DashboardStatus | null>(null);

  const handleSectionChange = (section: string) => {
    const cleanSection = section.replace(/^#+/, '').split('/')[0].split('?')[0];
    if (cleanSection === 'keyword-planner' || cleanSection === 'utility-manager') {
      navigate('/utility-manager');
      return;
    }
    setActiveSection(cleanSection);
  };
  usePageTracking({
    moduleName: 'SEO Manager',
    submoduleName: DASHBOARD_SECTIONS[activeSection] || 'Strategy Overview',
    routePath: `/dashboard#${activeSection}`,
  });

  useEffect(() => {
    const websiteUrl = localStorage.getItem('website_url');
    if (!websiteUrl) {
      navigate('/marketing-dashboard', { replace: true });
      return;
    }

    if (userId) {
      strategyService.getDashboardStatus(userId)
        .then((status) => {
          setDashboardStatus(status);
          localStorage.setItem('keywords_locked', String(status.keywords_locked));
          if (!status.keywords_locked) {
            navigate('/marketing-dashboard', { replace: true });
          }
        })
        .catch((err) => console.error('Failed to fetch dashboard status:', err));
    }
  }, [userId]);

  useEffect(() => {
    // Support hash-based section routing.
    if (location.hash) {
      const section = location.hash.replace('#', '');
      handleSectionChange(section);
    }
    // Support state-based section routing.
    if ((location.state as any)?.section) {
      handleSectionChange((location.state as any).section);
    }
  }, [location.hash, location.state]);

  // Support custom event for section switching (e.g. from notification bell)
  useEffect(() => {
    const handleSwitch = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.section) {
        handleSectionChange(customEvent.detail.section);
      }
    };
    window.addEventListener('switch_dashboard_section', handleSwitch);
    return () => window.removeEventListener('switch_dashboard_section', handleSwitch);
  }, []);

  const handleTooltipShow = (title: string, content: string) => {
    setTooltipContent({ title, content });
    setShowTooltip(true);
  };

  const refreshDashboardStatus = () => {
    if (userId) {
      strategyService.getDashboardStatus(userId)
        .then((status) => {
          setDashboardStatus(status);
          localStorage.setItem('keywords_locked', String(status.keywords_locked));
        })
        .catch((err) => console.error('Failed to fetch dashboard status:', err));
    }
  };

  // Fetch dashboard status when section changes
  useEffect(() => {
    refreshDashboardStatus();
  }, [activeSection]);

  const renderContent = () => {
    // Gate: if keywords are not locked, show placeholder for restricted sections
    if (GATED_SECTIONS[activeSection] && dashboardStatus && !dashboardStatus.keywords_locked) {
      return (
        <LockedPlaceholder
          sectionName={GATED_SECTIONS[activeSection]}
          onNavigate={() => navigate('/utility-manager')}
        />
      );
    }

    switch (activeSection) {
      case 'strategy-dashboard':
        return <StrategyDashboard onTooltipShow={handleTooltipShow} setActiveSection={handleSectionChange} dashboardStatus={dashboardStatus} />;
      case 'as-is-state':
        return <AsIsState onTooltipShow={handleTooltipShow} />;
      case 'goal-setting':
        return <GoalSetting onTooltipShow={handleTooltipShow} />;
      case 'action-plan':
        return <ActionPlan onTooltipShow={handleTooltipShow} />;
      case 'implementation-overview':
        return <ImplementationOverview onTooltipShow={handleTooltipShow} onNavigate={handleSectionChange} darkMode={darkMode} />;
      case 'blog':
        return <Blog onTooltipShow={handleTooltipShow} onBack={() => handleSectionChange('on-page-content')} darkMode={darkMode} userId={userId} />;
      case 'governance-overview':
        return <GovernanceOverviewDashboard onTooltipShow={handleTooltipShow} />;
      case 'as-is-performance':
        return <GovernanceAsIsPerformance userId={userId} onTooltipShow={handleTooltipShow} />;
      case 'goals-tracking':
        return <GoalsTracking userId={userId} onTooltipShow={handleTooltipShow} />;
      case 'action-plan-tracking':
        return <ActionPlanTracking onTooltipShow={handleTooltipShow} />;
      case 'competitor-benchmark':
        return <CompetitorBenchmark userId={userId} darkMode={darkMode} onTooltipShow={handleTooltipShow} />;
      case 'insight-navigator':
        return <InsightNavigator onTooltipShow={handleTooltipShow} />;
      case 'alerts':
        return <AlertsPanel onTooltipShow={handleTooltipShow} />;
      case 'faq':
        return <FAQ onTooltipShow={handleTooltipShow} onBack={() => handleSectionChange('on-page-content')} darkMode={darkMode} />;
      case 'on-page-content':
        return <OnPageContent onTooltipShow={handleTooltipShow} onNavigate={handleSectionChange} darkMode={darkMode} />;
      case 'image-optimizer':
        return <ImageOptimizer lockedKeywords={LOCKED_KEYWORDS} onBack={() => handleSectionChange('on-page-content')} darkMode={darkMode} />;
      case 'meta-title-generator':
        return <MetaTitleGenerator lockedKeywords={LOCKED_KEYWORDS} onBack={() => handleSectionChange('on-page-content')} darkMode={darkMode} />;
      case 'off-page-content':
        return <OffPageContent onTooltipShow={handleTooltipShow} onNavigate={handleSectionChange} darkMode={darkMode} />;
      case 'image-submission':
        return <ImageSubmissionWrapper onBack={() => handleSectionChange('off-page-content')} darkMode={darkMode} />;
      case 'classified-ads':
        return <ClassifiedAdsWrapper onBack={() => handleSectionChange('off-page-content')} darkMode={darkMode} />;
      case 'pdf-submission':
        return <PDFSubmissionWrapper onBack={() => handleSectionChange('off-page-content')} darkMode={darkMode} userId={userId} />;
      case 'web2-submission':
        return <Web2ArticleWrapper onBack={() => handleSectionChange('off-page-content')} darkMode={darkMode} />;
      case 'social-bookmarking':
        return <SocialBookmarkingDetail onBack={() => handleSectionChange('off-page-content')} darkMode={darkMode} />;
      case 'profile-business-listing':
        return <ProfileBusinessListingDetail onBack={() => handleSectionChange('off-page-content')} darkMode={darkMode} />;
      case 'directory-submission':
        return <DirectorySubmissionDetail onBack={() => handleSectionChange('off-page-content')} darkMode={darkMode} />;
      case 'qa-submission':
        return <QaSubmissionDetail onBack={() => handleSectionChange('off-page-content')} darkMode={darkMode} />;
      case 'optimization-overview':
        return <OptimizationOverview onTooltipShow={handleTooltipShow} onNavigate={handleSectionChange} />;
      case 'ecosystem-monitor':
        return <EcosystemMonitor onTooltipShow={handleTooltipShow} />;
      case 'foresight-recommends':
        return <ForesightRecommends onTooltipShow={handleTooltipShow} />;
      default:
        return <StrategyDashboard onTooltipShow={handleTooltipShow} setActiveSection={handleSectionChange} dashboardStatus={dashboardStatus} />;
    }
  };

  return (
    <div className={`h-screen overflow-hidden ${darkMode ? 'dark bg-gray-900' : 'bg-gray-50'} transition-colors duration-300`}>
      <div className="flex h-full">
        <Sidebar
          activeSection={activeSection}
          setActiveSection={handleSectionChange}
          darkMode={darkMode}
          dashboardStatus={dashboardStatus}
        />

        <div className="flex-1 flex flex-col">
          <Header
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            onTooltipShow={handleTooltipShow}
          />

          <main className="flex-1 p-6 overflow-auto">
            {renderContent()}
          </main>
        </div>
      </div>

      {showTooltip && (
        <SEOTooltip
          title={tooltipContent.title}
          content={tooltipContent.content}
          onClose={() => setShowTooltip(false)}
          darkMode={darkMode}
        />
      )}
    </div>
  );
}
